import { findD3NodeById } from './utils/hierarchyUtils.js';
import { apiGet } from './utils/apiClient.js';
import type { CirclePackNode } from '../shared/types.js';
import { findNodeById } from './panel/utils.js';
import { renderRoleReferences } from '../shared/roleReferences.js';
import { roleMap } from './state.js';
import { t } from './utils/i18n.js';

type CircleData = {
    name?: string;
    purpose?: string;
    accountabilities?: string[];
    responsibilities?: string[];
    id?: number;
};

type AncestorInfo = {
    name: string;
    purpose?: string;
    accountabilities?: string[];
    responsibilities?: string[];
};

function hasContent(data: any): boolean {
    return !!(
        (data.purpose && data.purpose.trim()) ||
        (data.accountabilities && data.accountabilities.length > 0) ||
        (data.responsibilities && data.responsibilities.length > 0)
    );
}

function collectAncestors(circle: SVGCircleElement): AncestorInfo[] {
    const ancestors: AncestorInfo[] = [];
    let current = (circle as any).__data__?.parent;
    
    while (current) {
        const ancestorData = current.data || current;
        if (hasContent(ancestorData)) {
            ancestors.push({
                name: ancestorData.name || 'Unnamed',
                purpose: ancestorData.purpose,
                accountabilities: ancestorData.accountabilities || [],
                responsibilities: ancestorData.responsibilities || []
            });
        }
        current = current.parent;
    }
    
    return ancestors;
}

function ensureInfoBox(chartElement: HTMLElement): HTMLElement {
    let infoBox = chartElement.querySelector('.hover-info') as HTMLElement;
    if (!infoBox) {
        infoBox = document.createElement('div');
        infoBox.className = 'hover-info';
        chartElement.appendChild(infoBox);
    }
    return infoBox;
}

function extractData(circle: SVGCircleElement): CircleData {
    const payload = (circle as any).__data__?.data || (circle as any).__data__ || {};
    return {
        name: payload.name || payload.data?.name,
        purpose: payload.purpose || payload.data?.purpose,
        accountabilities: payload.accountabilities || payload.data?.accountabilities || [],
        responsibilities: payload.responsibilities || payload.data?.responsibilities || []
    };
}

function renderInfo(infoBox: HTMLElement, data: CircleData, ancestors: AncestorInfo[]): void {
    const name = data.name || 'Unnamed';
    const purpose = data.purpose || '';
    const accountabilities = Array.isArray(data.accountabilities) ? data.accountabilities : [];
    const responsibilities = Array.isArray(data.responsibilities) ? data.responsibilities : [];

    const columns: { name: string; purpose: string; accountabilities: string[]; responsibilities: string[] }[] = [
        { name, purpose, accountabilities, responsibilities }
    ];

    ancestors.forEach(ancestor => {
        columns.push({
            name: ancestor.name,
            purpose: ancestor.purpose || '',
            accountabilities: ancestor.accountabilities || [],
            responsibilities: ancestor.responsibilities || []
        });
    });

    const sizeClass = ancestors.length > 0 ? 'small' : 'large';

    infoBox.innerHTML = '';
    const columnsDiv = document.createElement('div');
    columnsDiv.className = 'hover-info__columns';
    
    columns.forEach(col => {
        const columnDiv = document.createElement('div');
        columnDiv.className = 'hover-info__column';
        
        const nameDiv = document.createElement('div');
        nameDiv.className = `hover-info__name hover-info__name--${sizeClass}`;
        nameDiv.textContent = col.name;
        
        const purposeDiv = document.createElement('div');
        purposeDiv.className = `hover-info__purpose hover-info__purpose--${sizeClass}`;
        if (col.purpose) {
            purposeDiv.innerHTML = renderRoleReferences(col.purpose, roleMap);
        } else {
            const em = document.createElement('em');
            em.textContent = t('hover.noPurpose');
            purposeDiv.appendChild(em);
        }
        
        const accountabilitiesSection = document.createElement('div');
        accountabilitiesSection.className = `hover-info__section hover-info__section--${sizeClass}`;
        const accountabilitiesTitle = document.createElement('div');
        accountabilitiesTitle.className = 'hover-info__section-title';
        accountabilitiesTitle.textContent = t('hover.accountabilities');
        accountabilitiesSection.appendChild(accountabilitiesTitle);
        
        if (col.accountabilities.length) {
            const ul = document.createElement('ul');
            ul.className = 'hover-info__list';
            col.accountabilities.forEach(item => {
                const li = document.createElement('li');
                li.innerHTML = renderRoleReferences(item, roleMap);
                ul.appendChild(li);
            });
            accountabilitiesSection.appendChild(ul);
        } else {
            const emptyDiv = document.createElement('div');
            emptyDiv.className = 'hover-info__empty';
            emptyDiv.textContent = t('hover.none');
            accountabilitiesSection.appendChild(emptyDiv);
        }
        
        const responsibilitiesSection = document.createElement('div');
        responsibilitiesSection.className = `hover-info__section hover-info__section--${sizeClass}`;
        const responsibilitiesTitle = document.createElement('div');
        responsibilitiesTitle.className = 'hover-info__section-title';
        responsibilitiesTitle.textContent = t('hover.responsibilities');
        responsibilitiesSection.appendChild(responsibilitiesTitle);
        
        if (col.responsibilities.length) {
            const ul = document.createElement('ul');
            ul.className = 'hover-info__list';
            col.responsibilities.forEach(item => {
                const li = document.createElement('li');
                li.innerHTML = renderRoleReferences(item, roleMap);
                ul.appendChild(li);
            });
            responsibilitiesSection.appendChild(ul);
        } else {
            const emptyDiv = document.createElement('div');
            emptyDiv.className = 'hover-info__empty';
            emptyDiv.textContent = t('hover.none');
            responsibilitiesSection.appendChild(emptyDiv);
        }
        
        columnDiv.appendChild(nameDiv);
        columnDiv.appendChild(purposeDiv);
        columnDiv.appendChild(accountabilitiesSection);
        columnDiv.appendChild(responsibilitiesSection);
        columnsDiv.appendChild(columnDiv);
    });
    
    infoBox.appendChild(columnsDiv);
    
    // Attach click handlers to role reference links
    attachHoverInfoRoleReferenceHandlers(infoBox);
}

/**
 * Attach click handlers to role reference links in the hover info
 */
function attachHoverInfoRoleReferenceHandlers(infoBox: HTMLElement): void {
    const references = infoBox.querySelectorAll('.role-reference');
    references.forEach((ref) => {
        ref.addEventListener('click', async (event) => {
            event.stopPropagation();
            const roleId = parseInt(ref.getAttribute('data-role-id') || '0');
            if (roleId && (window as any).chartInstance) {
                const chartInstance = (window as any).chartInstance;
                const dataRoot = chartInstance.data();
                const hierarchyRoot = (dataRoot as any)?.__dataNode;
                
                if (hierarchyRoot) {
                    const d3Node = findD3NodeById(hierarchyRoot, roleId);
                    if (d3Node) {
                        chartInstance.zoomToNode(d3Node.data, { __dataNode: d3Node });
                    }
                }
            }
        });
    });
}

function positionInfoBox(infoBox: HTMLElement, event: Event, chartElement: HTMLElement): void {
    if (!(event instanceof MouseEvent)) return;
    const offset = 12;
    const rect = chartElement.getBoundingClientRect();
    const computed = getComputedStyle(infoBox);
    const declaredWidth = parseFloat(computed.width) || parseFloat(infoBox.style.width) || 0;
    const declaredHeight = parseFloat(computed.height) || parseFloat(infoBox.style.height) || 0;

    const boxWidth = Math.max(infoBox.offsetWidth || 0, declaredWidth, 320);
    const boxHeight = Math.max(infoBox.offsetHeight || 0, declaredHeight, 120);
    const pad = 4;

    let left = event.clientX - rect.left + offset;
    let top = event.clientY - rect.top + offset;

    const maxLeft = Math.max(0, rect.width - boxWidth - pad);
    const maxTop = Math.max(0, rect.height - boxHeight - pad);

    left = Math.min(Math.max(0, left), maxLeft);
    top = Math.min(Math.max(0, top), maxTop);

    infoBox.style.left = `${left}px`;
    infoBox.style.top = `${top}px`;
}

/**
 * Handle right-click to zoom to parent of currently displayed role
 */
function attachZoomToParentHandler(chartElement: HTMLElement, chartInstance?: any): void {
    if (!chartInstance) return;
    
    // Guard against multiple attachments
    if ((chartElement as any)._zoomToParentAttached) return;
    (chartElement as any)._zoomToParentAttached = true;
    
    // Attach to chart element using capture phase to intercept before SVG/circles
    chartElement.addEventListener('contextmenu', async (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Get the currently displayed node from the panel
        const panelContent = document.querySelector('.role-panel__content') as HTMLElement;
        const currentNodeId = panelContent?.dataset?.nodeId;
        
        if (!currentNodeId) {
            // No role displayed in panel, do nothing
            return;
        }

        // Use chart's data to get the full hierarchy with d3 nodes
        const dataRoot = chartInstance.data();
        const hierarchyRoot = (dataRoot as any)?.__dataNode;
        
        if (!hierarchyRoot) {
            return;
        }

        // Find the current node in the d3 hierarchy
        const currentD3Node = findD3NodeById(hierarchyRoot, parseInt(currentNodeId, 10));
        
        // Get the parent node
        const parentD3Node = currentD3Node?.parent;
        
        if (!parentD3Node || !parentD3Node.data) {
            return;
        }

        const parentId = parentD3Node.data.id;
        
        // Fetch fresh data and update panel (same pattern as role references)
        try {
            const freshRoles = await apiGet<CirclePackNode | CirclePackNode[]>('/api/roles');
            const freshData = Array.isArray(freshRoles) ? freshRoles[0] : freshRoles;
            const freshNodeData = findNodeById(freshData, parentId);
            
            if (freshNodeData && typeof (window as any).displayNodeData === 'function') {
                (window as any).displayNodeData(freshNodeData, false);
            }
        } catch (error) {
            console.error('Error fetching fresh data for panel:', error);
        }
        
        // Zoom to the parent node
        chartInstance.zoomToNode(parentD3Node.data, { __dataNode: parentD3Node });
    }, { capture: true, passive: false });
}

function wireCircle(circle: SVGCircleElement, infoBox: HTMLElement, chartElement: HTMLElement, chartInstance?: any): void {
    if ((circle as any)._hoverInfoAttached) return;
    (circle as any)._hoverInfoAttached = true;

    let hoverTimer: number | null = null;
    let lastEvent: MouseEvent | null = null;

    const isPinned = () => (infoBox as any)._isPinned === true;

    const show = (event: Event) => {
        if (isPinned()) return; // Don't show new tooltip if one is pinned
        
        lastEvent = event as MouseEvent;
        if (hoverTimer) {
            clearTimeout(hoverTimer);
        }
        hoverTimer = window.setTimeout(() => {
            if (isPinned()) return; // Double-check before showing
            
            const data = extractData(circle);
            const ancestors = collectAncestors(circle);
            renderInfo(infoBox, data, ancestors);
            infoBox.style.visibility = 'hidden';
            infoBox.style.display = 'block';
            positionInfoBox(infoBox, lastEvent || event, chartElement);
            infoBox.style.visibility = 'visible';
            
            // Store reference to current circle for pinning
            (infoBox as any)._currentCircle = circle;
        }, 5000);
    };

    const move = (event: Event) => {
        if (isPinned()) return; // Don't reposition pinned tooltip
        
        lastEvent = event as MouseEvent;
        if (infoBox.style.display !== 'none') {
            positionInfoBox(infoBox, event, chartElement);
        }
    };

    const hide = () => {
        if (isPinned()) return; // Don't hide if pinned
        
        if (hoverTimer) {
            clearTimeout(hoverTimer);
            hoverTimer = null;
        }
        infoBox.style.display = 'none';
    };

    const pin = (event: Event) => {
        if (infoBox.style.display === 'none') return; // Only pin if visible
        if ((infoBox as any)._currentCircle !== circle) return; // Only pin if this circle's tooltip is showing
        
        event.stopPropagation(); // Prevent event bubbling
        (infoBox as any)._isPinned = true;
        infoBox.style.border = '2px solid #0066cc';
        infoBox.style.pointerEvents = 'auto';
    };

    const unpin = (event: Event) => {
        event.stopPropagation(); // Prevent event bubbling
        (infoBox as any)._isPinned = false;
        (infoBox as any)._currentCircle = null;
        infoBox.style.border = '1px solid #e0e0e0';
        infoBox.style.pointerEvents = 'none';
        infoBox.style.display = 'none';
    };

    circle.addEventListener('mouseenter', show);
    circle.addEventListener('mousemove', move);
    circle.addEventListener('mouseleave', hide);
    circle.addEventListener('click', pin);
    
    // Only attach unpin listener once to the info box
    if (!(infoBox as any)._unpinAttached) {
        (infoBox as any)._unpinAttached = true;
        infoBox.addEventListener('click', unpin);
    }
}

export function attachHoverInfo(chartElement: HTMLElement, chartInstance?: any): void {
    if (!chartElement) return;

    if (getComputedStyle(chartElement).position === 'static') {
        chartElement.style.position = 'relative';
    }

    // Store chart instance reference for tooltip control
    if (chartInstance) {
        (chartElement as any)._chartInstance = chartInstance;
    }

    const infoBox = ensureInfoBox(chartElement);
    const svg = chartElement.querySelector('svg');
    const mainGroup = svg?.querySelector('g');

    const attachAll = () => {
        chartElement.querySelectorAll('circle').forEach(circle => wireCircle(circle as SVGCircleElement, infoBox, chartElement, chartInstance));
    };

    attachAll();

    const target = mainGroup || svg || chartElement;
    const observer = new MutationObserver(() => attachAll());
    observer.observe(target, { childList: true, subtree: true });
    
    // Attach right-click handler to SVG instead of chartElement
    if (svg) {
        attachZoomToParentHandler(svg as any, chartInstance);
    } else {
        // Fallback to chartElement if SVG not found yet
        attachZoomToParentHandler(chartElement, chartInstance);
    }
}
